import { createContext, useContext } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../../services/api';
import { queryKeys } from '../../../services/queries';

export const reviewIsActive = (review) => ['queued', 'processing'].includes(review?.status);
export const HeartbeatContextScope = createContext('signed-out');
export function heartbeatContextScope(user) {
	const id = user?._id || user?.id;
	return id ? `${id}:${user.teamOwner?._id || user.teamOwner || id}` : 'signed-out';
}
export const contextKey = (accountId, scope = 'signed-out') => ['heartbeat', accountId, 'context', scope];

export function invalidateHeartbeatReading(qc, accountId, runId, scope = 'signed-out') {
	const keys = [['heartbeat', accountId], ['heartbeat', 'index'], ['heartbeat', 'context', scope], ['accounts'], ['dashboard']];
	if (runId) keys.push(queryKeys.heartbeatRun(runId));
	return Promise.all(keys.map((queryKey) => qc.invalidateQueries({ queryKey })));
}

export function useHeartbeatContext(accountId, enabled, revision) {
	const scope = useContext(HeartbeatContextScope);
	return useQuery({
		queryKey: revision ? [...contextKey(accountId, scope), { revision }] : contextKey(accountId, scope),
		queryFn: async () => (await api.get(`/api/heartbeat/accounts/${accountId}/context`, ...(revision ? [{ revision }] : []))).data,
		enabled: !!accountId && enabled,
		staleTime: 0,
	});
}

export function useContextCatalog(params) {
	const scope = useContext(HeartbeatContextScope);
	return useQuery({ queryKey: ['heartbeat', 'context', scope, params], queryFn: async () => (await api.get('/api/heartbeat/context', { ...params, limit: 25 })).data });
}

export function useContextHistory(accountId, beforeRevision, enabled) {
	const scope = useContext(HeartbeatContextScope);
	return useQuery({ queryKey: [...contextKey(accountId, scope), 'history', beforeRevision], queryFn: async () => (await api.get(`/api/heartbeat/accounts/${accountId}/context/history`, { beforeRevision, limit: 20 })).data, enabled: !!accountId && enabled });
}

export function useContextCorrections(accountId, cursor, enabled) {
	const scope = useContext(HeartbeatContextScope);
	return useQuery({ queryKey: [...contextKey(accountId, scope), 'corrections', cursor], queryFn: async () => (await api.get(`/api/heartbeat/accounts/${accountId}/context/corrections`, { cursor, limit: 20 })).data, enabled: !!accountId && enabled });
}

export function useRestoreContextNote() {
	const scope = useContext(HeartbeatContextScope);
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async ({ accountId, ...body }) => (await api.post(`/api/heartbeat/accounts/${accountId}/context/restore`, body)).data,
		onSuccess: async (data, { accountId }) => { qc.setQueryData(contextKey(accountId, scope), data.context); await invalidateHeartbeatReading(qc, accountId, undefined, scope); },
	});
}

export function useSaveHeartbeatContext(accountId) {
	const scope = useContext(HeartbeatContextScope);
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (body) => (await api.put(`/api/heartbeat/accounts/${accountId}/context`, body)).data,
		onSuccess: async (data, body) => {
			qc.setQueryData(contextKey(accountId, scope), data.context);
			await invalidateHeartbeatReading(qc, accountId, body.runId, scope);
		},
	});
}

export function useReviewHeartbeatContext(accountId, runId, correction = false) {
	const scope = useContext(HeartbeatContextScope);
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (body) => (await api.post(`/api/heartbeat/runs/${runId}/${correction ? 'reply-correction' : 'review-context'}`, body)).data,
		onSuccess: async (review) => {
			qc.setQueryData(queryKeys.heartbeatRun(runId), (previous) => previous ? { ...previous, contextReview: review } : previous);
			await invalidateHeartbeatReading(qc, accountId, runId, scope);
		},
	});
}

export function describeContextError(error, correction = false) {
	if (error?.response?.status === 409) {
		if (!correction) return 'The saved context changed since you opened it. Your draft is still here. Copy any text you want to keep, then reload saved context.';
		const reason = error.response.data?.error;
		return typeof reason === 'string' && reason.trim() ? reason : 'This review could not start. Your current reading is still available. Please try again.';
	}
	return correction ? "We couldn't request this review. Your current reading is still available. Please try again." : "We couldn't save the context. Your draft is still here. Please try again.";
}
